/**
 * The welcome tour's two versions, as data: which screen each stop is on,
 * what it points at, and what it says. The engine is `Tour.tsx`.
 *
 * - "setup" for an account that has no store yet, or whose stores still have
 *   required setup steps left: where each step is done.
 * - "review" for an account where a store is ready (someone set it up, and the
 *   person joining approves what goes out): the draft-ready email, the review
 *   screen, and where to see what is coming and how it went.
 *
 * A stop only ever points. The tour never presses Approve, Send test or any
 * other button that does something; the last stop's "Open your draft" only
 * goes to the draft.
 */

import type { HelpArticle } from "@/lib/help-slugs";
import { setupSteps, type Overview } from "@/lib/setup";

/** Raise when the tour changes enough that everyone should see it again. */
export const TOUR_VERSION = 1;

export type TourKind = "setup" | "review";

/** What a tour's stops are resolved against when it starts. */
export interface TourContext {
  kind: TourKind;
  /** The store the tour shows: the first ready one for "review", else the first. */
  storeId: string | null;
  /** That store's name, as Home shows it. */
  storeName: string | null;
  /** How many drafts wait on that store. */
  draftsWaiting: number;
  /** The oldest draft waiting for approval, opened on the review screen. */
  draftId: string | null;
  /** Where "a draft is ready" emails go (Brand's notification address, else its reply-to); null when unknown. */
  /** The people told when a draft is ready, as Brand lists them; empty when unknown. */
  notifyEmails: string[];
  /** Whether a help bubble is on the page: the app's own, or the host site's around the frame. */
  helpBubble: boolean;
  /** The next setup step still to do, for the last stop of "setup". */
  nextStep: string | null;
  /** Where that step is done, and its button's words, for the last stop's button. */
  nextAction: { label: string; to: string } | null;
}

type Text = string | ((ctx: TourContext, total: number) => string);

export interface TourStop {
  id: string;
  title: string;
  body: Text;
  /** Where the stop is shown; none leaves the person where they are. */
  route?: (ctx: TourContext) => string;
  /**
   * What the stop points at: CSS selectors tried in order, the first with something on screen
   * winning (one comma selector would pick by document order instead). None, or nothing on
   * screen, puts the card in the middle.
   */
  target?: string | string[];
  learnMore?: HelpArticle;
  /** Left out when this says no. */
  when?: (ctx: TourContext) => boolean;
  /** The last stop's main button, when it should lead somewhere: it goes there and finishes the tour. */
  action?: (ctx: TourContext) => { label: string; to: string } | null;
}

/** A stop as shown: its words worked out for this account, its targets as a list. */
export type ShownStop = Omit<TourStop, "body" | "target"> & { body: string; target: string[] };

const campaigns = (ctx: TourContext, tab?: string) => `/campaigns?store=${ctx.storeId ?? ""}${tab ? `&tab=${tab}` : ""}`;
const review = (ctx: TourContext) => `${campaigns(ctx)}&campaign=${ctx.draftId ?? ""}`;
const hasStore = (ctx: TourContext) => ctx.storeId !== null;
const hasDraft = (ctx: TourContext) => ctx.draftId !== null;
const storeName = (ctx: TourContext) => ctx.storeName || "Your store";
const NOTIFY_SETTING = 'Brand → "Tell these people about new drafts"';

/** "a@x.com", "a@x.com and b@y.com", "a@x.com, b@y.com and c@z.com". */
export function listOf(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const WELCOME: TourStop = {
  id: "welcome",
  title: "Welcome to Lettrova",
  body: (_ctx, total) =>
    `Lettrova writes email campaigns from your own store's products and keeps your email service up to date with your customers. Nothing is ever sent until you approve it. This tour is ${total} short stops, ${
      total <= 7 ? "a minute or two" : "about two minutes"
    }. You can skip it and take it again from Home.`,
  route: () => "/home",
  learnMore: "welcome-tour",
};

const SETUP: TourStop[] = [
  WELCOME,
  {
    id: "checklist",
    title: "Your setup checklist",
    body: (ctx) =>
      hasStore(ctx)
        ? "Home lists what is left to do, in order, with a button for the next step and a few words on how. Once everything is ticked, this card shows what is waiting for you and when the next email goes out."
        : "Home always shows what to do next. Right now that's connecting your store; after that, a checklist of what is left, with a button for each step and a few words on how.",
    route: () => "/home",
    target: '[data-tour="home-store"]',
    learnMore: "setup-checklist",
  },
  {
    id: "stores",
    title: "Stores: your shop and your email service",
    body:
      "Connect your store here, then your email service (Mailchimp, Klaviyo or Brevo) and the audience to send to. From then on Lettrova copies your products and customers across by itself. Only customers who agreed to marketing become subscribers.",
    route: () => "/stores",
    // A connected store's card, else the connect form.
    target: ['[data-tour="stores-card"]', '[data-tour="stores-connect"]'],
    learnMore: "who-becomes-a-subscriber",
  },
  {
    id: "brand",
    title: "Brand: who your emails are from",
    body: "Add your sender name and reply-to address, your colors and a few words about how your shop talks. Every draft is written with these.",
    route: (ctx) => campaigns(ctx, "brand"),
    target: "#campaigns-tab-brand",
    learnMore: "brand-settings",
    when: hasStore,
  },
  {
    id: "schedules",
    title: "Schedules: drafts on a regular rhythm",
    body:
      "Choose how often to email and what about. Lettrova writes each draft ahead of time and emails you when it is ready. Nothing goes out until you approve it.",
    route: (ctx) => campaigns(ctx, "schedules"),
    target: "#campaigns-tab-schedules",
    learnMore: "schedules-and-campaign-kinds",
    when: hasStore,
  },
  {
    id: "next",
    title: "You're ready to start",
    body: (ctx) =>
      `${ctx.nextStep ? `Your next step: ${ctx.nextStep.toLowerCase()}. ` : ""}Home always shows what is left, with a button for it. Look for "Learn more" on any screen when something isn't clear${
        ctx.helpBubble ? ", or ask in the help bubble at the bottom right" : ""
      }.`,
    route: () => "/home",
    action: (ctx) => ctx.nextAction,
  },
];

const REVIEW: TourStop[] = [
  WELCOME,
  {
    id: "home",
    title: "Home: what's waiting and what's next",
    body: (ctx) =>
      `${storeName(ctx)}'s card says ${
        ctx.draftsWaiting === 0
          ? "when a draft is waiting for your approval"
          : ctx.draftsWaiting === 1
            ? "a draft is waiting for your approval"
            : `${ctx.draftsWaiting} drafts are waiting for your approval`
      }, and when the next email goes out. It's the place to look first.`,
    route: () => "/home",
    target: '[data-tour="home-store"]',
  },
  {
    id: "email",
    title: "An email tells you when a draft is ready",
    body: (ctx) =>
      `When a draft is written, Lettrova emails "Ready for your approval" to ${
        ctx.notifyEmails.length > 0 ? `${listOf(ctx.notifyEmails)} (set under ${NOTIFY_SETTING})` : `the addresses under ${NOTIFY_SETTING}`
      }. Its button opens the draft. Reply to it in your own words ("make it shorter") and the draft is changed for you; a reply never approves or sends anything.`,
    learnMore: "draft-ready-email",
  },
  {
    id: "list",
    title: "Campaigns: every draft in one list",
    body: (ctx) =>
      hasDraft(ctx)
        ? "Here is the draft waiting for you, listed with everything scheduled and sent. Next, the tour opens it."
        : "Drafts waiting for you are listed here with everything scheduled and sent. Click one to open it.",
    route: (ctx) => campaigns(ctx),
    target: ['[data-tour="campaign-row"][data-status="pending_approval"]', '[data-tour="campaign-list"]', "#campaigns-tab-campaigns"],
    learnMore: "review-and-edit",
  },
  // A draft is waiting: the review screen itself.
  {
    id: "preview",
    title: "The preview, or the assistant",
    body:
      'On the right is the email as your customers will see it, on a desktop or a phone. Switch to Assistant to say what to change in your own words, like "friendlier" or "fewer products". It changes the draft, and every change can be undone.',
    route: review,
    target: ['[data-tour="review-panel-switch"]', '[data-tour="review-panel"]'],
    learnMore: "what-the-assistant-does",
    when: hasDraft,
  },
  {
    id: "edit",
    title: "Or change it yourself",
    body: "Edit the subject line, the preview text and the words here, then save. Further down you can change which products are shown, and their order.",
    route: review,
    target: '[data-tour="review-edit"]',
    learnMore: "review-and-edit",
    when: hasDraft,
  },
  {
    id: "test",
    title: "Send a test",
    body:
      'A copy in an inbox, as your customers will see it. A test goes only to the test recipients set in Brand, or an address you type, never to your customers. "Email me this draft" sends the "Ready for your approval" email again.',
    route: review,
    target: '[data-tour="review-test"]',
    learnMore: "test-sends",
    when: hasDraft,
  },
  {
    id: "approve",
    title: "Approve when you're happy",
    body:
      '"Approve & schedule" sends it at the time shown; "Send now" sends it straight away. Both ask you to confirm first. A sent email can\'t be taken back, so if a draft isn\'t right, change it or press "Skip this one": skipping is always fine.',
    route: review,
    target: '[data-tour="review-approve"]',
    learnMore: "approve-and-send",
    when: hasDraft,
  },
  // No draft waiting yet: the same in one card.
  {
    id: "review",
    title: "Reviewing a draft",
    body:
      "Opening a waiting draft shows the email as customers will see it. Change the words yourself or ask the assistant to, send yourself a test, then approve it, which asks you to confirm. Nothing is sent before that, and skipping a draft that isn't right is always fine.",
    route: (ctx) => campaigns(ctx),
    learnMore: "approve-and-send",
    when: (ctx) => !hasDraft(ctx),
  },
  {
    id: "tabs",
    title: "What's coming, and how it went",
    body:
      "Schedules are the regular emails, say every other Tuesday, and Plan holds one-off dates like a holiday sale. The draft for each arrives a couple of days before it is due. Results shows how each campaign did: opens, clicks and orders.",
    route: (ctx) => campaigns(ctx, "schedules"),
    target: ['[role="tablist"][aria-label="Campaign sections"]', "#campaigns-tab-schedules"],
    learnMore: "plan-tab",
  },
  {
    id: "done",
    title: "You're all set",
    body: (ctx) =>
      `${hasDraft(ctx) ? "Your draft is ready for you. " : ""}When something isn't clear, look for "Learn more" on the screen${
        ctx.helpBubble ? ", ask in the help bubble at the bottom right," : ""
      } or ask the Assistant. You can take this tour again from Home.`,
    route: () => "/home",
    learnMore: "joining-an-account",
    action: (ctx) => (hasDraft(ctx) ? { label: "Open your draft", to: review(ctx) } : null),
  },
];

/**
 * Which version fits: "review" once any store has every required step done,
 * else "setup". Also picks the store it shows and the setup step still to do.
 */
export function chooseTour(
  overview: Overview | null,
): Pick<TourContext, "kind" | "storeId" | "storeName" | "nextStep" | "nextAction" | "draftsWaiting"> {
  const stores = overview?.stores ?? [];
  const ready = stores.find((s) => setupSteps(s).every((st) => st.optional || st.done));
  const store = ready ?? stores[0] ?? null;
  const next = store ? setupSteps(store).find((st) => !st.optional && !st.done) : undefined;
  return {
    kind: ready ? "review" : "setup",
    storeId: store?.id ?? null,
    storeName: store?.name ?? null,
    nextStep: next?.label ?? (stores.length === 0 ? "Connect your store" : null),
    nextAction: next ? { label: next.action, to: next.to } : stores.length === 0 ? { label: "Connect your store", to: "/stores" } : null,
    draftsWaiting: ready?.drafts_waiting ?? 0,
  };
}

/** The stops of a tour, with the ones that don't apply left out and their words worked out. */
export function stopsFor(ctx: TourContext): ShownStop[] {
  const stops = (ctx.kind === "review" ? REVIEW : SETUP).filter((s) => !s.when || s.when(ctx));
  return stops.map((s) => ({
    ...s,
    body: typeof s.body === "function" ? s.body(ctx, stops.length) : s.body,
    target: s.target === undefined ? [] : Array.isArray(s.target) ? s.target : [s.target],
  }));
}
